import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  getMetadataFieldsByCustomer, 
  createMetadataField, 
  updateMetadataField, 
  deleteMetadataField 
} from '../services/api/metadataApi';

/**
 * Custom hook for managing metadata fields for a specific customer
 * @param {string} customerId - The ID of the customer
 * @returns {Object} Metadata fields state and CRUD operations
 */
export const useMetadataFields = (customerId) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use ref to prevent duplicate API calls in StrictMode
  const hasFetched = useRef(false);
  const currentCustomerId = useRef(customerId);

  useEffect(() => {
    // Reset when customerId changes
    if (currentCustomerId.current !== customerId) {
      currentCustomerId.current = customerId;
      hasFetched.current = false;
      setFields([]);
    }

    if (!hasFetched.current && customerId) {
      hasFetched.current = true;
      fetchFields();
    }
  }, [customerId]);

  const fetchFields = async () => {
    if (!customerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getMetadataFieldsByCustomer(customerId);
      
      // Handle 404/204 responses gracefully
      if (response.status === 204 || !response.data) {
        setFields([]);
      } else {
        setFields(response.data || []);
      }
    } catch (err) {
      // Handle 404 as empty list
      if (err.response?.status === 404) {
        setFields([]);
      } else {
        setError(err.response?.data?.message || 'Failed to fetch metadata fields');
        console.error('Error fetching metadata fields:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const createField = async (fieldData) => {
    try {
      setError(null);

      // Validate field key uniqueness within customer
      const existingField = fields.find(f => f.fieldKey === fieldData.fieldKey);
      if (existingField) {
        throw new Error('A metadata field with this key already exists for this customer');
      }

      // Automatically associate with customer ID
      const dataWithCustomer = {
        ...fieldData,
        customerId: customerId
      };

      const response = await createMetadataField(dataWithCustomer);
      
      // Refresh the list
      await fetchFields();
      
      return response.data;
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to create metadata field';
      setError(errorMessage);
      console.error('Error creating metadata field:', err);
      throw err;
    }
  };

  const updateField = async (id, fieldData) => {
    try {
      setError(null);

      // Validate field key uniqueness (excluding current field)
      if (fieldData.fieldKey) {
        const existingField = fields.find(f => f.fieldKey === fieldData.fieldKey && f.id !== id);
        if (existingField) {
          throw new Error('A metadata field with this key already exists for this customer');
        }
      }

      const response = await updateMetadataField(id, fieldData);
      
      // Refresh the list
      await fetchFields();
      
      return response.data;
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to update metadata field';
      setError(errorMessage);
      console.error('Error updating metadata field:', err);
      throw err;
    }
  };

  const deleteField = async (id) => {
    try {
      setError(null);

      await deleteMetadataField(id);
      
      // Refresh the list
      await fetchFields();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete metadata field');
      console.error('Error deleting metadata field:', err);
      throw err;
    }
  };

  const refreshFields = useCallback(async () => {
    hasFetched.current = false;
    await fetchFields();
  }, [customerId]);

  const exportFields = () => {
    const exportData = fields.map(field => ({
      FieldName: field.name,
      FieldKey: field.fieldKey,
      DataType: field.dataType.charAt(0).toUpperCase() + field.dataType.slice(1),
      Description: field.description || ''
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-${customerId}-metadata-fields.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadSampleJson = () => {
    const sampleData = [
      {
        "FieldName": "PurchaseOrderNumber",
        "FieldKey": "purchase_order_number",
        "DataType": "String",
        "Description": "Purchase order reference number"
      },
      {
        "FieldName": "CostCenter",
        "FieldKey": "cost_center",
        "DataType": "String",
        "Description": "Cost center code for accounting"
      },
      {
        "FieldName": "ProjectCode",
        "FieldKey": "project_code",
        "DataType": "String",
        "Description": "Project identifier for tracking"
      },
      {
        "FieldName": "ApprovalRequired",
        "FieldKey": "approval_required",
        "DataType": "Boolean",
        "Description": "Whether approval is required for this invoice"
      }
    ];

    const blob = new Blob([JSON.stringify(sampleData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-metadata-configuration.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const validateImportData = (configurations) => {
    const fieldsToImport = [];
    const invalidFields = [];

    for (const [index, config] of configurations.entries()) {
      // Validate mandatory fields
      const missingFields = [];
      if (!config.FieldName) missingFields.push('FieldName');
      if (!config.FieldKey) missingFields.push('FieldKey');
      if (!config.DataType) missingFields.push('DataType');

      // Validate Field Key format
      if (config.FieldKey && !/^[a-zA-Z0-9_]+$/.test(config.FieldKey)) {
        invalidFields.push(`Row ${index + 1}: Field Key can only contain letters, numbers, and underscores`);
        continue;
      }

      if (missingFields.length > 0) {
        invalidFields.push(`Row ${index + 1}: Missing ${missingFields.join(', ')}`);
        continue;
      }

      // Check if field already exists based on FieldKey
      const existingField = fields.find(f => f.fieldKey === config.FieldKey);

      // Create field from configuration
      const fieldData = {
        name: config.FieldName,
        fieldKey: config.FieldKey,
        dataType: config.DataType === 'numeric' ? 'number' : (typeof config.DataType === 'string' ? config.DataType.toLowerCase() : 'string'),
        description: config.Description || '',
        customerId: customerId
      };

      if (existingField) {
        fieldData.id = existingField.id;
        fieldData.isUpdate = true;
      }

      fieldsToImport.push(fieldData);
    }

    return { fieldsToImport, invalidFields };
  };

  const importFields = async (fieldsToImport) => {
    let importedCount = 0;
    const errors = [];

    for (const fieldData of fieldsToImport) {
      try {
        if (fieldData.isUpdate) {
          await updateMetadataField(fieldData.id, fieldData);
        } else {
          await createMetadataField(fieldData);
        }
        importedCount++;
      } catch (fieldError) {
        console.error('Error saving field:', fieldData.name, fieldError);
        errors.push(`${fieldData.name}: ${fieldError.message}`);
      }
    }

    // Refresh the list after import
    await fetchFields();

    return { importedCount, errors };
  };

  return {
    fields,
    loading,
    error,
    createField,
    updateField,
    deleteField,
    refreshFields,
    exportFields,
    downloadSampleJson,
    validateImportData,
    importFields
  };
};
